import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, getRefreshToken } from '@/auth/store';
import { logout as apiLogout, getMe } from '@/api/auth';
import { useQuery } from '@tanstack/react-query';
import TenantSwitcher from '@/components/TenantSwitcher';
import { hasModule, useEnabledModules } from '@/hooks/useEnabledModules';
import { usePermissions } from '@/auth/usePermissions';
import AutoRouteGuard from '@/auth/AutoRouteGuard';
import ErrorBoundary from '@/components/ErrorBoundary';
import {
  I18N_PREFIX,
  SIDEBAR_ITEMS,
  type SidebarItem,
} from './sidebarItems';

function resolveLabel(label: string, t: (key: string) => string): string {
  if (label.startsWith(I18N_PREFIX)) {
    return t(label.slice(I18N_PREFIX.length));
  }
  return label;
}

/**
 * Фільтрує SIDEBAR_ITEMS:
 *   - за enabled_modules (3.0.5.1c): link з module=null лишається,
 *     link з module що НЕ у Set — приховується.
 *   - за permissions (3.0.5.5): link з permission, якого user не має —
 *     приховується. OWNER/ADMIN бачать усе.
 *
 * Sections, після яких не лишилось видимих items до наступної section,
 * приховуються автоматично.
 */
export function filterSidebarItems(
  items: SidebarItem[],
  modulesState: ReturnType<typeof useEnabledModules>,
  permissions: ReturnType<typeof usePermissions>,
): SidebarItem[] {
  // 1. Видаляємо приховані link'и.
  const visibleLinks = items.filter((it) => {
    if (it.type !== 'link') return true; // section проходимо у крок 2
    // module filter
    if (it.module !== null && !hasModule(modulesState, it.module)) return false;
    // permission filter
    // Поки me ще loading — показуємо все (fast path для OWNER/ADMIN
    // спрацьовує тільки коли me.data є; до цього кнопки/links не мерехтять).
    if (permissions.isLoading) return true;
    if (it.permission === null) return true;
    return permissions.has(it.permission);
  });
  // 2. Видаляємо section'и, після яких нема link'ів (до наступної section
  //    або до кінця).
  const result: SidebarItem[] = [];
  for (let i = 0; i < visibleLinks.length; i++) {
    const item = visibleLinks[i]!;
    if (item.type === 'section') {
      // Перевіряємо, чи є хоча б один link після цієї section до наступної.
      let hasLinkAfter = false;
      for (let j = i + 1; j < visibleLinks.length; j++) {
        const next = visibleLinks[j]!;
        if (next.type === 'section') break;
        if (next.type === 'link') {
          hasLinkAfter = true;
          break;
        }
      }
      if (hasLinkAfter) result.push(item);
    } else {
      result.push(item);
    }
  }
  return result;
}

/**
 * Чи лежить шлях `pathname` всередині пункту `to` ПО МЕЖI СЕГМЕНТА.
 * Точний збіг або вкладений шлях (`to` + '/'). НЕ голий startsWith — щоб
 * '/positions' не збігся з '/pos'.
 */
export function pathMatches(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(to + '/');
}

interface MenuLink {
  to: string;
  label: string;
  match?: string[];   // додаткові префікси для активації (редактори з власним URL)
  // Шляхи, які НЕ підсвічують цей пункт, хоча й лежать «всередині» to.
  // Потрібно, коли вкладений шлях має ВЛАСНИЙ пункт меню (інакше підсвітились
  // би обидва): «Кабінет» /dps-cabinet vs «Створити документ» /dps-cabinet/new.
  exclude?: string[];
}
interface MenuGroup {
  label: string;       // вже resolved
  links: MenuLink[];
}

/** Чи активний пункт меню для поточного шляху: збіг по `to` АБО по будь-якому
 * `match`, окрім шляхів з `exclude` (вони мають власний пункт меню). */
export function linkMatches(pathname: string, l: MenuLink): boolean {
  if ((l.exclude ?? []).some((e) => pathMatches(pathname, e))) return false;
  if (pathMatches(pathname, l.to)) return true;
  return (l.match ?? []).some((m) => pathMatches(pathname, m));
}

/**
 * Згрупувати плаский visibleItems (section-маркери + links) у розділи верхнього
 * меню. Links ДО першої section (Головна) — у безсекційну групу з порожнім label
 * (рендериться окремим пунктом, не dropdown).
 */
export function groupForTopMenu(
  items: SidebarItem[],
  t: (key: string) => string,
): { standalone: MenuLink[]; groups: MenuGroup[] } {
  const standalone: MenuLink[] = [];
  const groups: MenuGroup[] = [];
  let current: MenuGroup | null = null;
  for (const it of items) {
    if (it.type === 'section') {
      current = { label: resolveLabel(it.label, t), links: [] };
      groups.push(current);
    } else if (current) {
      current.links.push({ to: it.to, label: resolveLabel(it.label, t), match: it.match });
    } else {
      standalone.push({ to: it.to, label: resolveLabel(it.label, t), match: it.match });
    }
  }
  // Прибрати порожні групи (фільтр міг лишити section без links — підстраховка).
  return { standalone, groups: groups.filter((g) => g.links.length > 0) };
}

export default function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const clear = useAuth((s) => s.clear);
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const modulesState = useEnabledModules();
  const permissions = usePermissions();
  const location = useLocation();
  // Відкритий dropdown зверху (клік по розділу). Працює НЕЗАЛЕЖНО від лівого
  // субменю — щоб підпункти були доступні навіть коли ліве згорнуте.
  const [dropdownGroup, setDropdownGroup] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);

  // Закрити dropdown при зміні маршруту (перейшли на сторінку).
  useEffect(() => { setDropdownGroup(null); }, [location.pathname]);
  // Закрити dropdown при кліку поза меню.
  useEffect(() => {
    if (dropdownGroup === null) return;
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setDropdownGroup(null);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [dropdownGroup]);
  // Ліве субменю: ширина (ресайз) + згорнутість — persist у localStorage.
  const [subWidth, setSubWidth] = useState<number>(() => {
    const v = Number(localStorage.getItem('submenu.width'));
    return v >= 160 && v <= 420 ? v : 220;
  });
  const [subCollapsed, setSubCollapsed] = useState<boolean>(
    () => localStorage.getItem('submenu.collapsed') === '1');

  useEffect(() => { localStorage.setItem('submenu.width', String(subWidth)); }, [subWidth]);
  useEffect(() => {
    localStorage.setItem('submenu.collapsed', subCollapsed ? '1' : '0');
  }, [subCollapsed]);

  async function handleLogout() {
    const r = getRefreshToken();
    if (r) {
      try {
        await apiLogout(r);
      } catch {
        /* ignore */
      }
    }
    clear();
    navigate('/login', { replace: true });
  }

  const visibleItems = filterSidebarItems(SIDEBAR_ITEMS, modulesState, permissions);
  const { standalone, groups } = groupForTopMenu(visibleItems, t);

  // ЛIВЕ субменю слідує за МАРШРУТОМ (де юзер реально перебуває), а НЕ за кліком на
  // розділ. Клік на розділ відкриває лише dropdown зверху (dropdownGroup); ліва
  // панель оновлюється тільки коли юзер перейшов у підпункт цього розділу.
  //
  // Збіг шляху рахуємо ПО МЕЖI СЕГМЕНТА (не голий startsWith): шлях або точно
  // дорівнює link, або починається з `link + '/'`. Інакше '/positions' хибно
  // збігся б з '/pos' (POS-термінал у розділі Роздріб) і ліве меню стрибало б
  // на чужий розділ.
  // `/` (Загальне) тепер пункт групи «Головна» — НЕ виключаємо його, інакше на
  // головній сторінці ліве субменю не показувалось би. pathMatches для to='/'
  // дає true ЛИШЕ при точному pathname==='/' (startsWith('//') ніколи), тож
  // '/' не «з'їдає» інші сторінки.
  const activeGroup = groups.find((g) =>
    g.links.some((l) => linkMatches(location.pathname, l))) ?? null;

  return (
    <div className={`app${subCollapsed || !activeGroup ? ' app--sub-collapsed' : ''}`}
         style={!subCollapsed && activeGroup
           ? { ['--sub-w' as string]: `${subWidth}px` } : undefined}>
      <header className="app__header">
        <h1>{t('app.title')}</h1>
        <div className="right">
          <TenantSwitcher />
          {me.data && <span style={{ opacity: 0.85 }}>{me.data.full_name}</span>}
          <button onClick={handleLogout}>{t('auth.logout')}</button>
        </div>
      </header>

      {/* Верхні розділи: клік відкриває dropdown зверху (працює завжди, навіть коли
          ліве згорнуте) ТА робить розділ активним для лівого субменю. */}
      <nav className="app__topnav" ref={navRef}>
        {standalone.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.to === '/'} className="app__topnav-link">
            {l.label}
          </NavLink>
        ))}
        {groups.map((g) => {
          const isDropdownOpen = dropdownGroup === g.label;
          return (
            <div key={g.label} className="app__topnav-group">
              <button
                type="button"
                className={`app__topnav-trigger${
                  isDropdownOpen || activeGroup?.label === g.label ? ' is-open' : ''}`}
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
                onClick={() => setDropdownGroup(isDropdownOpen ? null : g.label)}
              >
                {g.label}
                <span className="app__topnav-caret">▾</span>
              </button>
              <div className={`app__topnav-dropdown${isDropdownOpen ? ' is-open' : ''}`}
                   role="menu">
                {g.links.map((l) => (
                  <NavLink key={l.to} to={l.to} className="app__topnav-item" role="menuitem"
                           onClick={() => setDropdownGroup(null)}>
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Ліве субменю — підпункти активного розділу, ресайзбл + згортання. */}
      {activeGroup && !subCollapsed && (
        <aside className="app__submenu" style={{ width: subWidth }}>
          <div className="app__submenu-head">
            <span>{activeGroup.label}</span>
            <button type="button" className="app__submenu-toggle"
                    title="Згорнути" onClick={() => setSubCollapsed(true)}>‹</button>
          </div>
          <div className="app__submenu-items">
            {activeGroup.links.map((l) => (
              <NavLink key={l.to} to={l.to}
                       className={({ isActive }) =>
                         `app__submenu-item${isActive || linkMatches(location.pathname, l) ? ' active' : ''}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
          {/* Ресайз-ручка */}
          <div
            className="app__submenu-resizer"
            onMouseDown={(e) => {
              e.preventDefault();
              const startX = e.clientX;
              const startW = subWidth;
              const onMove = (ev: MouseEvent) => {
                const w = Math.min(420, Math.max(160, startW + ev.clientX - startX));
                setSubWidth(w);
              };
              const onUp = () => {
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onUp);
            }}
          />
        </aside>
      )}

      {/* Кнопка розгорнути субменю (коли згорнуте, але розділ активний) */}
      {activeGroup && subCollapsed && (
        <button type="button" className="app__submenu-expand"
                title={`Показати: ${activeGroup.label}`}
                onClick={() => setSubCollapsed(false)}>›</button>
      )}

      <main className="app__main">
        <AutoRouteGuard>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </AutoRouteGuard>
      </main>
    </div>
  );
}
